import { offerImageUrl } from "@/components/chat/ChatCommerceCard";
import { EvvyMarkdown } from "@/components/common/evvyMarkdown";
import type { ProgramProduct } from "@/services/commerceProgramOffers";

// Figma: https://www.figma.com/design/fPDiaxbsQDLapw3qiul2us/-PROD--EvvyAI?node-id=2852-10071

// Shop Care's label for a product the provider chooses after the consult.
const RX_LABEL = "Determined by provider";
const SUB_LINE = "text-ds-body-xsmall-regular text-ds-text-light-on-white";
// Figma's Highlight/Salmon, Secondary/Silverfish and Highlight/Mint; only salmon has a ds token.
const CATEGORY_TAGS: Record<
  ProgramProduct["category"],
  { label: string; color: string }
> = {
  TREAT: { label: "Treat", color: "bg-ds-all-colors-salmon" },
  PROTECT: { label: "Protect", color: "bg-evvy-silverfish" },
  RELIEF: { label: "Relief", color: "bg-highlighter-mint" },
};

/** A program's products: image, name, what it is, its category and what the add does with it. */
export default function ChatProgramItems({
  id,
  products,
}: {
  id: string;
  products: ProgramProduct[];
}) {
  return (
    <ul
      id={id}
      className="m-0 flex list-none flex-col gap-[var(--chat-space-04)]"
    >
      {products.map((product) => {
        const image = offerImageUrl(product);
        const tag = CATEGORY_TAGS[product.category];
        return (
          // The dashed line above each row is Figma's divider between rows.
          <li
            key={product.treatment_product_id}
            className="m-0 flex items-center justify-between gap-[var(--chat-space-03)] border-0 border-t border-dashed border-ds-line-light-on-white pt-[var(--chat-space-04)]"
          >
            <div className="flex min-w-0 items-center gap-[var(--chat-space-04)]">
              {image ? (
                <img
                  src={image}
                  // Decorative: the name is printed beside it.
                  alt=""
                  className={`size-[48px] shrink-0 rounded-ds-corner-small object-cover ${product.subscribed ? "opacity-50" : ""}`}
                />
              ) : (
                <span className="size-[48px] shrink-0 rounded-ds-corner-small bg-ds-background-creme" />
              )}
              <div className="flex min-w-0 flex-col">
                <span
                  className={`text-ds-body-xsmall-regular ${product.subscribed ? "text-ds-text-light-on-white" : "text-ds-text-primary"}`}
                >
                  {product.name}
                </span>
                {product.provider_determined ? (
                  <span className={SUB_LINE}>{RX_LABEL}</span>
                ) : (
                  product.description && (
                    // Catalog text, rendered as Shop Care renders it; a div, since markdown can be block-level.
                    <div className={`${SUB_LINE} [&_p]:m-0`}>
                      <EvvyMarkdown>{product.description}</EvvyMarkdown>
                    </div>
                  )
                )}
                {product.subscribed && (
                  <span className={SUB_LINE}>
                    Already subscribed · not included
                  </span>
                )}
                {product.in_cart_subscription && (
                  <span className={SUB_LINE}>
                    In your cart as a subscription
                  </span>
                )}
              </div>
            </div>
            <span
              className={`inline-flex h-[20px] shrink-0 items-center rounded-[35px] border border-solid border-ds-ui-elements-black/5 px-[10px] text-ds-header-subheader uppercase leading-[20px] text-ds-text-primary ${tag.color}`}
            >
              {tag.label}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
